/* @flow */
import * as React from "react";
import styled from "styled-components";
import Gettext from "../common/Gettext";

import formatTime from "./formatTime";
import Money from "../Money";
import { tournamentCurrency } from "../Icons/Kuchka";

import type { Deal as DealObject } from "../../redux/CommonTypes";
import type { StockPoint } from "../../redux/reducer/points";

const Tab = styled.div`
  position: relative;
  width: 174px;
  height: 71px;
  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: 15px;
  padding-right: 15px;
  color: ${({ theme }) => theme.foreground};
  border-bottom: 1px dashed ${props => props.theme.mainSeparator};
  background-color: ${props => props.theme.mainDark};
  background-repeat: no-repeat;
  background-position: 100% 0;
`;

// prettier-ignore
const TabTitle = styled.div`
  display: table-cell;
`;

const Line = styled.div`
  display: table;
  width: 100%;
`;

const TableContainer = styled.div`
  display: table;
  width: 100%;
  height: 100%;
`;

const TableCell = styled.div`
  display: table-cell;
  vertical-align: middle;
`;

// prettier-ignore
const TimeContainer = styled.div`
  padding-bottom: 2px;
`;

const Time = styled.div`
  font-size: ${({ theme }) => (theme.locale === "ar" ? "12px" : "12px")};
  color: ${props => props.theme.accent};
`;

const Title = styled.div`
  display: table-cell;
  font-size: ${({ theme }) => (theme.locale === "ar" ? "14px" : "14px")};
  text-transform: uppercase;
`;

const Bet = styled.div`
  display: table-cell;
  font-size: ${({ theme }) => (theme.locale === "ar" ? "14px" : "14px")};
  font-weight: 700;
  text-align: right;
  color: ${props => (props.win ? "#60a31c" : "#e74c3c")};
`;

const BetCount = styled.div`
  display: table;
  width: 100%;
  padding-top: 5px;
  font-weight: 400;
  font-size: ${({ theme }) => (theme.locale === "ar" ? "12px" : "12px")};
  line-height: 1;
`;

const DealTypeContainer = styled.div`
  display: table-cell;
  vertical-align: top;
`;

const BetDirection = styled.div`
  display: inline-block;
  vertical-align: middle;
  width: 9px;
  height: 7px;
  margin-right: 4px;
  line-height: 0;
  color: #60a31c;
`;

const BetDirectionText = styled.div`
  display: inline-block;
  vertical-align: middle;
  font-weight: inherit;
  font-size: inherit;
  line-height: inherit;
  color: ${({ theme }) => theme.foreground};
`;

const BetCountText = styled.div`
  display: table-cell;
  vertical-align: top;
  padding-top: 1px;
  font-weight: inherit;
  font-size: inherit;
  line-height: inherit;
  text-align: right;
  color: ${({ theme }) => theme.foreground};
`;

const ProgressContainer = styled.div`
  position: absolute;
  bottom: 0px;
  left: 0px;
  width: 100%;
  height: 4px;
  background-color: ${props => props.theme.mainLight};
`;

const Progress = styled.div`
  height: 4px;
  width: 0%;
  background-color: ${props => props.theme.accent};
  transition: all 0.1s ease;
`;

function UpIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      viewBox="0 0 9 7"
      fill="currentColor"
    >
      <path d="M4.5,0.494L0.779,6.921H8.226L4.5,0.494" />
    </svg>
  );
}

function DownIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      viewBox="0 0 9 7"
      fill="#e74c3c"
    >
      <path d="M4.5,7.384L0.779,0.958H8.226L4.5,7.384" />
    </svg>
  );
}

function ProgressBar(props) {
  const barWidth = Math.round(props.value * 10000) / 100;

  return (
    <ProgressContainer>
      <Progress style={{ width: `${barWidth}%` }} />
    </ProgressContainer>
  );
}

type Props = DealObject & {
  currentPoint: StockPoint
};

function Deal(props: Props) {
  const point = props.currentPoint;
  const now = point.date;

  const beginTime = parseInt(props.timestamp, 10) * 1000;
  const endTime = beginTime + props.bTime * 1000;

  const progress = (now - beginTime) / (props.bTime * 1000);

  const profit = (props.amount * parseInt(props.percent || 0, 10)) / 100;

  const isWinning = props.bidType
    ? props.value <= point.value
    : props.value >= point.value;

  let resultingAmount = isWinning ? `+${profit}` : `-${props.amount}`;
  if (props.value === point.value) {
    resultingAmount = 0;
  }

  const stock = props.stock.substr(
    0,
    props.stock.length > 6 ? 6 : props.stock.length
  );

  const currency = props.tradeMode === "tournament" ? tournamentCurrency : null;

  return (
    <Tab>
      <TableContainer>
        <TableCell>
          <TimeContainer>
            <Time>{formatTime(endTime - now)}</Time>
          </TimeContainer>

          <Line>
            <TabTitle>
              <Title>{stock}</Title>
            </TabTitle>

            <Bet win={isWinning}>
              <Money currency={currency}>{resultingAmount}</Money>
            </Bet>
          </Line>

          <BetCount>
            <DealTypeContainer>
              <BetDirection>
                {props.bidType ? <UpIcon /> : <DownIcon />}
              </BetDirection>
              <BetDirectionText>
                {props.bidType ? (
                  <Gettext>Upper</Gettext>
                ) : (
                  <Gettext>lower</Gettext>
                )}
              </BetDirectionText>
            </DealTypeContainer>
            <BetCountText>
              <Money currency={currency}>{props.amount}</Money>
            </BetCountText>
          </BetCount>

          <ProgressBar value={progress} />
        </TableCell>
      </TableContainer>
    </Tab>
  );
}

export default Deal;



// WEBPACK FOOTER //
// ./src/components/Tabs/Deal.js